import { Profile, ThreemaProps } from '@bp/bp-graphql-types';
import { Button, Table, TableColumns, ThreemaIcon, useDefaultSorting } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { ThreemaCredentials } from 'client/gql/graphql';
import { useTranslation } from 'react-i18next';

export const ThreemaTableFragment = graphql(`
  fragment ThreemaTable on Query {
    listThreemaCredentials(where: { uuid: $organizationUuid }) {
      password
      username
      usage
      __typename
      uuid
    }
    threemas(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      profilesConnection {
        __typename
        edges {
          __typename
          node {
            uuid
            __typename
            firstName
            lastName
          }
          properties {
            __typename
            threemaId
            enabled
          }
        }
      }
    }
  }
`);

type ThreemaTableType = Pick<Profile, 'firstName' | 'lastName'> &
  Pick<ThreemaProps, 'threemaId' | 'enabled'> &
  Pick<ThreemaCredentials, 'password' | 'username' | 'usage' | 'uuid'>;

type ThreemaTableProps = {
  query: FragmentType<typeof ThreemaTableFragment> | undefined;
  handleAssignClick: (threemaProps: ThreemaProps) => void;
};

export const ThreemaTable = ({ query, handleAssignClick }: ThreemaTableProps) => {
  const { t } = useTranslation();
  const fragment = getFragmentData(ThreemaTableFragment, query);
  const threemaDataFromInternalDatabase = fragment?.threemas.flatMap((threema) =>
    threema.profilesConnection.edges.map((profile) => ({ ...profile.properties, ...profile.node })),
  );
  const data =
    fragment?.listThreemaCredentials.map((threema) => {
      const { __typename, ...threemaWithoutTypename } = threema;
      const { firstName, lastName, enabled } =
        threemaDataFromInternalDatabase?.find((internalThreema) => internalThreema.threemaId === threema.uuid) ?? {};
      return { ...threemaWithoutTypename, firstName, lastName, enabled };
    }) ?? [];

  const columns: TableColumns<ThreemaTableType>[] = [
    {
      header: t('common.firstName'),
      id: 'firstName',
      accessorKey: 'firstName',
    },
    {
      header: t('common.lastName'),
      id: 'lastName',
      accessorKey: 'lastName',
    },
    {
      header: t('threema.enabled'),
      id: 'enabled',
      accessorKey: 'enabled',
      type: 'boolean',
    },
    {
      header: t('common.username'),
      id: 'username',
      accessorKey: 'username',
    },
    {
      header: t('common.password'),
      id: 'password',
      accessorKey: 'password',
    },
    {
      header: 'ThreemaId',
      id: 'uuid',
      accessorKey: 'uuid',
    },
    {
      header: 'Usage',
      id: 'usage',
      accessorKey: 'usage',
      type: 'number',
    },
  ];

  const { sorting, onSortingChange } = useDefaultSorting();

  return (
    <Table<ThreemaTableType>
      breakpoint='580px'
      data-cy='threemaList'
      showActionBar
      isOnWhite={false}
      showBorderRadius
      showShadow
      showSort
      canScroll
      sorting={sorting}
      onSortingChange={onSortingChange}
      actionBarSettings={{
        showExpertFilter: true,
      }}
      emptyStateSettings={{
        icon: <ThreemaIcon />,
        padding: 'xl',
      }}
      columns={columns}
      data={data}
      lastColWidth='100px'
      lastCol={(row) => {
        const { uuid: threemaId, firstName, username: workUser, password: workPass } = row.original;
        const canAssign = !firstName;
        const threemaInput: ThreemaProps = {
          enabled: true,
          workUser,
          workPass,
          threemaId,
        };

        if (!canAssign || !threemaId) return null;

        return (
          <Button hierarchy='primary' onClick={() => handleAssignClick(threemaInput)} fullWidth>
            {t('threema.assign')}
          </Button>
        );
      }}
    />
  );
};
